import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
// CORRECCIÓN: Se define la URL base directamente para que el componente sea autocontenido.
import { API_BASE_URL } from '../App';

// --- Material-UI Imports ---
import {
    Container, Box, Button, Paper, TextField, Typography,
    Stack, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Dialog, DialogActions,
    DialogContent, DialogContentText, DialogTitle, Snackbar, Alert, CircularProgress, // Se añade Snackbar y Alert
    Tooltip, InputAdornment
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete'; // Se añade DeleteIcon
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import LanIcon from '@mui/icons-material/Lan'; // <-- ÍCONO AÑADIDO

// --- INTERFACES ---
interface CPANetwork {
    id: number;
    name: string;
    postback_url?: string | null;
    created_at: string;
    updated_at: string;
}

// --- Custom Confirmation Dialog Component ---
// CORRECCIÓN: Se re-integra el componente de diálogo de confirmación que había sido omitido.
const ConfirmationDialog: React.FC<{
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    children: React.ReactNode;
}> = ({ open, onClose, onConfirm, title, children }) => (
    <Dialog open={open} onClose={onClose}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent><Typography color="text.secondary">{children}</Typography></DialogContent>
        <DialogActions>
            <Button onClick={onClose}>Cancelar</Button>
            <Button onClick={onConfirm} color="error" variant="contained">Confirmar Eliminación</Button>
        </DialogActions>
    </Dialog>
);


// --- COMPONENTE PRINCIPAL ---
const CPANetworksPage: React.FC = () => {
    // --- ESTADOS ---
    const [cpaNetworks, setCpaNetworks] = useState<CPANetwork[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null);

    // State for the creation/editing form
    const [newName, setNewName] = useState<string>('');
    const [newPostbackUrl, setNewPostbackUrl] = useState<string>('');
    const [editingNetworkId, setEditingNetworkId] = useState<number | null>(null);

    // State for form validation error
    const [formValidationError, setFormValidationError] = useState<string | null>(null);

    // State for delete confirmation dialog
    const [deleteDialog, setDeleteDialog] = useState<{ open: boolean, id: number | null, name: string }>({ open: false, id: null, name: '' });

    // State for search term
    const [searchTerm, setSearchTerm] = useState('');

    const theme = useTheme();
    const headerCellStyle = { fontWeight: 'bold', backgroundColor: theme.palette.action.hover };

    // --- LÓGICA DE DATOS ---
    const fetchCPANetworks = useCallback(async () => {
        if (cpaNetworks.length === 0) setIsLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API_BASE_URL}/cpa-networks`);
            setCpaNetworks(response.data);
        } catch (err: any) {
            setError(err.response?.data?.message || err.message || 'Error al obtener redes CPA.');
        } finally {
            setIsLoading(false);
        }
    }, [cpaNetworks.length]);

    useEffect(() => {
        fetchCPANetworks();
    }, [fetchCPANetworks]);

    // --- MANEJADORES DE FORMULARIO ---
    const resetForm = () => {
        setEditingNetworkId(null);
        setNewName('');
        setNewPostbackUrl('');
        setFormValidationError(null);
    };

    const startEditing = (network: CPANetwork) => {
        setEditingNetworkId(network.id);
        setNewName(network.name);
        setNewPostbackUrl(network.postback_url || '');
        setFormValidationError(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setNotification(null);
        setFormValidationError(null);

        if (!newName.trim()) {
            setFormValidationError("El nombre de la Red CPA es obligatorio.");
            return;
        }

        const networkData = {
            name: newName,
            postback_url: newPostbackUrl || null,
        };

        const endpoint = editingNetworkId ? `/cpa-networks/${editingNetworkId}` : '/cpa-networks';
        const method = editingNetworkId ? 'PUT' : 'POST';

        try {
            await axios({ method, url: `${API_BASE_URL}${endpoint}`, data: networkData });
            setNotification({ type: 'success', message: `Red CPA ${editingNetworkId ? 'actualizada' : 'creada'} con éxito!` });
            resetForm();
            fetchCPANetworks();
        } catch (err: any) {
            setNotification({ type: 'error', message: err.response?.data?.message || 'Error desconocido al guardar.' });
        }
    };

    const handleDeleteConfirm = (network: CPANetwork) => {
        setDeleteDialog({ open: true, id: network.id, name: network.name });
    };

    const handleDelete = async () => {
        if (!deleteDialog.id) return;
        try {
            await axios.delete(`${API_BASE_URL}/cpa-networks/${deleteDialog.id}`);
            setNotification({ type: 'success', message: `Red CPA (ID: ${deleteDialog.id}) eliminada con éxito!` });
            fetchCPANetworks();
        } catch (err: any) {
            setNotification({ type: 'error', message: err.response?.data?.message || 'Error desconocido al eliminar.' });
        } finally {
            setDeleteDialog({ open: false, id: null, name: '' });
        }
    };

    const filteredCpaNetworks = useMemo(() => {
        if (!searchTerm) return cpaNetworks;
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        return cpaNetworks.filter(network =>
            String(network.id).includes(lowerCaseSearchTerm) ||
            network.name.toLowerCase().includes(lowerCaseSearchTerm) ||
            (network.postback_url || '').toLowerCase().includes(lowerCaseSearchTerm)
        );
    }, [cpaNetworks, searchTerm]);

    if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
    if (error && cpaNetworks.length === 0) return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Snackbar
                open={!!notification}
                autoHideDuration={6000}
                onClose={() => setNotification(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setNotification(null)} severity={notification?.type} sx={{ width: '100%' }}>
                    {notification?.message}
                </Alert>
            </Snackbar>

            <ConfirmationDialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, id: null, name: '' })}
                onConfirm={handleDelete}
                title="Confirmar Eliminación de Red CPA"
            >
                ¿Estás seguro de que quieres eliminar la red CPA "{deleteDialog.name}" (ID: {deleteDialog.id})? Esta acción es irreversible y podría afectar ofertas asociadas.
            </ConfirmationDialog>

            {/* --- CAMBIO: Título actualizado --- */}
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                <LanIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '2.5rem' }} />
                Gestión de Redes CPA
            </Typography>

            <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: '12px' }}>
                <Typography variant="h6" component="h2" gutterBottom color="text.primary">
                    {editingNetworkId ? 'Editar Red CPA' : 'Crear Nueva Red CPA'}
                </Typography>
                <Box component="form" onSubmit={handleSubmit} noValidate>
                    <Stack spacing={3}>
                        <TextField
                            name="network_name"
                            label="Nombre de la Red CPA"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            placeholder="Nombre de la red CPA"
                            required
                            fullWidth
                            variant="outlined"
                            error={!!formValidationError && !newName.trim()}
                            helperText={!!formValidationError && !newName.trim() ? "El nombre de la Red CPA es obligatorio." : ""}
                        />
                        <TextField
                            name="network_postback_url"
                            label="URL Postback (opcional)"
                            type="url"
                            value={newPostbackUrl}
                            onChange={(e) => setNewPostbackUrl(e.target.value)}
                            placeholder="https://postback.turedcpa.com?cid={click_id}"
                            fullWidth
                            variant="outlined"
                        />
                        <Stack direction="row" spacing={2}>
                            <Button type="submit" variant="contained">
                                {editingNetworkId ? 'Actualizar Red' : 'Crear Red'}
                            </Button>
                            {editingNetworkId && (
                                <Button type="button" variant="text" onClick={resetForm}>
                                    Cancelar
                                </Button>
                            )}
                        </Stack>
                    </Stack>
                </Box>
            </Paper>

            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2} sx={{ mt: 5, mb: 2 }}>
                <Typography variant="h5" component="h2" color="text.primary">Redes CPA Registradas</Typography>
                <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Buscar por ID, Nombre o URL Postback..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
                />
            </Stack>

            <Paper elevation={3} sx={{ overflow: 'hidden', borderRadius: '12px' }}>
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={headerCellStyle}>ID</TableCell>
                                <TableCell sx={headerCellStyle}>Nombre</TableCell>
                                <TableCell sx={headerCellStyle}>URL Postback</TableCell>
                                <TableCell sx={headerCellStyle}>Creado</TableCell>
                                <TableCell sx={headerCellStyle}>Actualizado</TableCell>
                                <TableCell align="right" sx={headerCellStyle}>Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredCpaNetworks.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3 }} >
                                        No hay redes CPA registradas.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredCpaNetworks.map((network) => (
                                    <TableRow key={network.id} hover>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{network.id}</TableCell>
                                        <TableCell sx={{ textTransform: 'none' }}>{network.name}</TableCell>
                                        <TableCell>
                                            {network.postback_url ? (
                                                <Tooltip title={network.postback_url}>
                                                    <a href={network.postback_url} target="_blank" rel="noopener noreferrer" style={{ color: theme.palette.primary.main, textDecoration: 'none' }}>
                                                        Ver URL
                                                    </a>
                                                </Tooltip>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">N/A</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(network.created_at).toLocaleString()}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(network.updated_at).toLocaleString()}</TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Editar Red CPA">
                                                <IconButton size="small" onClick={() => startEditing(network)} color="primary">
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Eliminar Red CPA">
                                                <IconButton size="small" onClick={() => handleDeleteConfirm(network)} color="error">
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Container>
    );
};

export default CPANetworksPage;